import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ConfirmProvider } from "./components/ui/confirm";
import { OnboardingProvider } from "./context/OnboardingContext";
import RankingPage from "./pages/RankingPage";
import { ThemeProvider } from "./context/ThemeContext";

// Pages
import AuthPage from "./pages/AuthPage";
import RecuperarPage from "./pages/RecuperarPage";
import MiCuerpoPage from "./pages/MiCuerpoPage";
import { dejarDestino, destinoTrasEntrar, leerDestino, limpiarDestino, panelDe } from "./lib/navegacion";
import { toast } from "sonner";
import { avisoDeVueltaSinSesion, puedeEntrarSola, vueltaSinSesion } from "./lib/vueltaDelEnlace";
import { ClientDashboard, ClientLayout } from "./pages/ClientDashboard";
import RoutinePage from "./pages/RoutinePage";
import EntrenoPage from "./pages/EntrenoPage";
import NutritionPage from "./pages/NutritionPage";
import MiSemanaPage from "./pages/MiSemanaPage";
import MiHorarioPage from "./pages/MiHorarioPage";
import MiDiaPage from "./pages/MiDiaPage";
import TodosLosAvisosPage from "./pages/TodosLosAvisosPage";
import ReportsPage from "./pages/ReportsPage";
import MessagesPage from "./pages/MessagesPage";
import ProfilePage from "./pages/ProfilePage";
import { AdminLayout } from "./pages/AdminDashboard";
import ClientDetailPage from "./pages/ClientDetailPage";
import LeadsPage from "./pages/LeadsPage";
import ChatbotPage from "./pages/ChatbotPage";
import SupplementsCatalogPage from "./pages/SupplementsCatalogPage";
import AdminMenusPage from "./pages/AdminMenusPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminPagosPage from "./pages/AdminPagosPage";
import AdminMessagesPage from "./pages/AdminMessagesPage";
import AdminRoutinesPage from "./pages/AdminRoutinesPage";
import SupplementsPage from "./pages/SupplementsPage";
import CheckInsPage from "./pages/CheckInsPage";
import MacroCalculatorClientPage from "./pages/MacroCalculatorClientPage";
import HistoricoDeMacrosPage from "./pages/HistoricoDeMacrosPage";
import RevisionPage from "./pages/RevisionPage";
import AjustePersonalizadoPage from "./pages/AjustePersonalizadoPage";
import FoodSearchPage from "./pages/FoodSearchPage";
import AdminFoodSuggestionsPage from "./pages/AdminFoodSuggestionsPage";
import AdminPlansPage from "./pages/AdminPlansPage";
import AdminAjustesPage from "./pages/AdminAjustesPage";
import AdminAccesosPage from "./pages/AdminAccesosPage";
import AdminRelojPage from "./pages/AdminRelojPage";
import AdminSimuladorPage from "./pages/AdminSimuladorPage";
import AdminDocumentacionPage from "./pages/AdminDocumentacionPage";
import AdminTareasPage from "./pages/AdminTareasPage";
import AdminPanelesPage from "./pages/AdminPanelesPage";
import GestorDeClientesPage from "./pages/GestorDeClientesPage";
import QuestionnairePage from "./pages/QuestionnairePage";
import WelcomePage from "./pages/WelcomePage";
import PlanesPage from "./pages/PlanesPage";
import RenovacionPage from "./pages/RenovacionPage";
import QuizVentaPage from "./pages/QuizVentaPage";
import InstallPrompt from "./components/InstallPrompt";
import FechaDePrueba from "./components/FechaDePrueba";
import BarraActuandoComo from "./components/BarraActuandoComo";
import FondoFotos from "./components/FondoFotos";
import Logo12EN12 from "./components/Logo12EN12";
import { seVendenPlanes, hayTestDeVenta, esLaVueltaDeUnPago } from './lib/ventaEnLaApp';
import { leTocaElCuestionario, sinAccesoALaApp } from './lib/puertasDelCliente';


// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { isAuthenticated, user, loading, profile, perfilNoCargado } = useAuth();
    const location = useLocation();
    const aqui = `${location.pathname}${location.search}`;
    // Ya ha llegado a donde iba al entrar (`lib/navegacion`): se olvida, para que la próxima
    // vez que entre en esta pestaña no le vuelva a mandar aquí.
    React.useEffect(() => {
        if (isAuthenticated && leerDestino() === aqui) limpiarDestino();
    }, [isAuthenticated, aqui]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        // Con la dirección que se pedía, para volver a ella al entrar (16-10, `lib/navegacion`):
        // el botón de un correo que lleva a una ficha no puede acabar en el panel.
        dejarDestino(aqui);
        return <Navigate to="/auth" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user?.role)) {
        // A SU PANEL, NO SIEMPRE AL DE CLIENTE (I9, 12-10). El entrenador que escribía
        // /admin/pagos o /admin/reloj (solo de administrador) caía en /dashboard y, sin
        // cuestionario hecho, en el cuestionario de alta de cliente, con "Cerrar sesión"
        // como única salida. Ahora vuelve al panel del equipo.
        return <Navigate to={panelDe(user)} replace />;
    }

    // SI LE TOCA EL CUESTIONARIO, AL CUESTIONARIO, en todas las pantallas del cliente
    // (Enrique, 04-11; prueba 2.1). Vivía solo en el panel (`ClientLayout`), así que lo que
    // cuelga fuera de /dashboard (/mi-cuerpo, /renovacion, /welcome) se abría con el
    // cuestionario sin hacer. La regla es la de siempre (`lib/puertasDelCliente`), con sus
    // excepciones (Mensajes, Mi perfil). Fuera quedan el propio cuestionario y la vuelta de
    // un pago a /planes, que confirma el pago y ya le lleva ella.
    const esDelEquipo = user?.role === 'admin' || user?.role === 'trainer';
    const exento = location.pathname === '/questionnaire'
        || (location.pathname === '/planes' && esLaVueltaDeUnPago(location.search))
        // La vuelta de Stripe de la renovación (Mi perfil): /renovacion confirma el pago.
        || (location.pathname === '/renovacion' && new URLSearchParams(location.search).get('renovado') === 'ok');
    if (!esDelEquipo && !exento) {
        const sinAcceso = sinAccesoALaApp({ loading, esDelEquipo, perfilNoCargado, profile });
        if (leTocaElCuestionario(profile, sinAcceso, location.pathname)) {
            return <Navigate to="/questionnaire" replace />;
        }
    }

    return children;
};

// Capability Route - gate a page by a plan capability (ver lib/planAccess.js).
// Si el plan del usuario no la habilita, redirige al inicio del panel.
const CapabilityRoute = ({ cap, children }) => {
    const { can, loading } = useAuth();
    if (loading) return null;
    if (!can(cap)) return <Navigate to="/dashboard" replace />;
    return children;
};

// Lo mismo, pero con un interruptor del panel en vez de una capacidad del plan. Quitar la
// entrada del menú esconde la puerta, no la pantalla: quien tenga la dirección guardada
// entra igual. Con esto, mientras el interruptor esté apagado, la dirección devuelve al
// inicio (Francisco, 26-08, para el asistente).
const PantallaRoute = ({ nombre, children }) => {
    const { pantalla, loading } = useAuth();
    if (loading) return null;
    if (!pantalla(nombre)) return <Navigate to="/dashboard" replace />;
    return children;
};

// A dónde va una dirección que no existe. Al login SOLO si no hay sesión; si el usuario ya
// está dentro se le deja en su panel, que es lo que esperaría de un enlace que no lleva a
// ninguna parte. Mientras se comprueba la sesión no se redirige a nada: hacerlo antes de
// saber quién es era la otra forma de acabar en el login sin motivo.
const ADondeSea = () => {
    const { isAuthenticated, user, loading } = useAuth();
    if (loading) return null;
    if (!isAuthenticated) return <Navigate to="/auth" replace />;
    return <Navigate to={panelDe(user)} replace />;
};

// Public Route - Redirect if authenticated
const PublicRoute = ({ children }) => {
    const { isAuthenticated, user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
        );
    }

    if (isAuthenticated) {
        // Iba a algún sitio antes de identificarse (el test de nivel, el botón de un correo):
        // manda ahí, no al panel. Ver lib/navegacion: sin esto la venta del test se perdía.
        return <Navigate to={destinoTrasEntrar(user)} replace />;
    }

    return children;
};

// LA VUELTA DEL ENLACE DE PAGO, PARA QUIEN LLEGA SIN SESIÓN (11-10). Quien paga el enlace
// que manda el equipo vuelve a `/planes?pago=ok&session_id=...`, y `/planes` está detrás
// del login: el que aún no tiene cuenta (o no ha entrado en este navegador) acababa en
// /auth y su pago no se confirmaba desde ningún sitio. Aquí, que se monta antes que
// cualquier ruta, se guarda la dirección con la que llegó y, cuando se sabe que no hay
// sesión, se confirma igual (lib/vueltaDelEnlace: el servidor le pregunta a Stripe). Con
// sesión no hace nada: lo hace la pantalla de planes, que además mira si le cambia el plan.
const AVISO_DE_LA_VUELTA = 'vuelta-del-enlace';
//
// SIN PARPADEOS AL ENTRAR SOLA (Francisco, 16-10: «primero muestra el login, luego el inicio
// y recién el formulario; ese parpadeo no puede pasar, pero sí una pantalla que diga pago
// aprobado y un botón de iniciar cuestionario»). Si la vuelta lleva el secreto de entrar, no
// se pinta ninguna pantalla mientras se confirma (`children` espera). Si entra, «Pago aprobado»
// y de ahí al cuestionario; si no, las pantallas de siempre con su aviso.
const VueltaDelEnlace = ({ children }) => {
    const { isAuthenticated, loading, entrarConSesion } = useAuth();
    const navigate = useNavigate();
    const [llegada] = React.useState(() => window.location.search);
    // 'esperando' (se confirma), 'aprobado' (ha entrado) o null (nada que tapar).
    const [tapa, setTapa] = React.useState(() => (puedeEntrarSola(llegada) ? 'esperando' : null));
    const hecho = React.useRef(false);
    React.useEffect(() => {
        if (loading || hecho.current) return;
        hecho.current = true;
        if (isAuthenticated) { setTapa(null); return; }
        // EL AVISO SE QUEDA PUESTO (14-10, PAG-09): no se va solo a los 6 segundos, que es lo
        // que tarda a veces la confirmación; lo cierra él o se quita al entrar (abajo).
        // Y SI ACABA DE NACER SU CUENTA CON ESTE PAGO, ENTRA YA (16-10): el servidor da su
        // sesión una vez. Si no la da (cuenta de antes, ya usada, tarde), lo de siempre.
        vueltaSinSesion(llegada).then(async ({ apuntado, sesion }) => {
            if (sesion) {
                try {
                    await entrarConSesion(sesion.access_token, sesion.user);
                    // El destino que dejó la ruta protegida al llegar sin sesión es esta misma
                    // vuelta (con su `session_id` y su `entrada`): no va a llegar a ella, así
                    // que se olvida aquí. Si no, el próximo acceso en esta pestaña volvía a
                    // un pago viejo (revisor, 16-10).
                    limpiarDestino();
                    setTapa('aprobado');
                    return;
                } catch (e) {
                    console.error('[vuelta del enlace] no se pudo entrar con la sesión', e);
                }
            }
            setTapa(null);
            const aviso = avisoDeVueltaSinSesion(llegada, apuntado);
            if (!aviso) return;
            (aviso.confirmado ? toast.success : toast.info)(aviso.frase, { id: AVISO_DE_LA_VUELTA, duration: Infinity, siempre: true });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- una vez, al saber si hay sesión
    }, [loading]);
    // Ya ha entrado: "entra con tu correo" sobra.
    React.useEffect(() => {
        if (isAuthenticated) toast.dismiss(AVISO_DE_LA_VUELTA);
    }, [isAuthenticated]);
    const empezar = () => {
        // Primero la dirección y luego las pantallas: así no se monta la de la vuelta.
        navigate('/questionnaire', { replace: true });
        setTapa(null);
    };
    if (tapa === 'esperando') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-4" data-testid="vuelta-confirmando">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
                <p className="text-muted-foreground text-sm">Confirmando tu pago…</p>
            </div>
        );
    }
    // PLANTILLA A, ÉXITO (rediseño del onboarding, 18-10; docs/diseño/design_handoff_onboarding).
    // Mismo botón y misma acción que antes («Iniciar cuestionario»); cambia el aspecto y el texto.
    if (tapa === 'aprobado') {
        return (
            <div className="dark min-h-screen relative overflow-x-hidden flex flex-col text-[#f2f0ed]" data-testid="vuelta-aprobado"
                style={{ background: '#0b0b0b', fontFamily: "'Barlow', sans-serif" }}>
                <FondoFotos />
                <div className="relative z-10 px-5 md:px-7 pt-5 pb-3"><Logo12EN12 size="sm" /></div>
                <div className="relative z-10 flex-1 flex justify-center px-6 pt-12 pb-16">
                    <div className="w-full max-w-[600px] flex flex-col items-center text-center gap-6 pt-4">
                        <div className="w-24 h-24 rounded-full bg-[#2a1b10] flex items-center justify-center"
                            style={{ boxShadow: '0 0 0 10px rgba(255,106,31,.08)' }} aria-hidden="true">
                            <svg width="44" height="44" viewBox="0 0 44 44">
                                <path d="M11 23l7 7 15-16" stroke="#ff6a1f" strokeWidth="4.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <h1 className="font-heading text-[40px] md:text-[48px] font-bold leading-none [text-wrap:balance]">
                            Pago realizado correctamente
                        </h1>
                        <div className="w-full flex gap-3.5 items-start text-left bg-[#161616] border border-[#262626] rounded-2xl p-[18px]">
                            <div className="flex-none w-10 h-10 rounded-xl bg-[#222] flex items-center justify-center" aria-hidden="true">
                                <svg width="20" height="20" viewBox="0 0 20 20">
                                    <rect x="2.5" y="4.5" width="15" height="11" rx="2" stroke="#ff6a1f" strokeWidth="1.7" fill="none" />
                                    <path d="M3 5.5l7 5.5 7-5.5" stroke="#ff6a1f" strokeWidth="1.7" fill="none" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-base font-bold">Revisa tu correo electrónico</span>
                                <span className="text-[15px] leading-[1.45] text-[#b5b0aa]">
                                    Encontrarás un email con tus datos de acceso, que podrás utilizar para iniciar sesión en la plataforma cuando quieras.
                                </span>
                            </div>
                        </div>
                        <span className="text-[17px] font-semibold">Ya puedes comenzar el cuestionario inicial</span>
                        <button type="button" onClick={empezar} data-testid="vuelta-iniciar-cuestionario"
                            className="w-full h-14 rounded-[14px] bg-[#ff6a1f] hover:bg-[#ff7d3a] text-[#140a04] text-[17px] font-extrabold inline-flex items-center justify-center gap-2.5">
                            Comenzar cuestionario
                            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Navigate to="/auth" replace />} />
            <Route
                path="/auth"
                element={
                    <PublicRoute>
                        <AuthPage />
                    </PublicRoute>
                }
            />

            {/* El quiz de venta va SIN sesion y sin PublicRoute: "ve su resultado sin dar
                el correo" (parte 10), y PublicRoute redirige al panel al que ya ha
                entrado alguna vez, que es justo a quien se le manda el enlace. */}
            {/* CERRADO DESDE EL 15-10 (`lib/ventaEnLaApp`): por dirección, a la entrada. */}
            <Route path="/test" element={hayTestDeVenta() ? <QuizVentaPage /> : <Navigate to="/" replace />} />

            {/* Recuperar la contraseña. SIN PublicRoute a propósito: si no puedes entrar,
                no puedes estar dentro para arreglarlo, y PublicRoute echa de aquí a quien
                tenga sesión abierta -- justo el que llega desde el enlace del correo en
                el móvil donde sigue logueado. */}
            <Route path="/recuperar" element={<RecuperarPage />} />

            {/* Cuestionario inicial obligatorio (antes del plan) */}
            <Route
                path="/questionnaire"
                element={
                    <ProtectedRoute>
                        <QuestionnairePage />
                    </ProtectedRoute>
                }
            />

            {/* Bienvenida tras el cuestionario (muestra macros + primer paso) */}
            <Route
                path="/welcome"
                element={
                    <ProtectedRoute>
                        <WelcomePage />
                    </ProtectedRoute>
                }
            />

            {/* Onboarding */}
            <Route
                path="/onboarding"
                element={
                    <ProtectedRoute>
                        {/* Una sola pantalla de planes. /onboarding es la vieja: se hizo
                            para los planes de antes y listaba cualquier plan activo con
                            precio, así que al añadir los tres niveles cogió el Nivel 3
                            con botón de pagar, cuando el documento dice que se contrata
                            hablando. Además le faltaban los textos de los niveles nuevos.
                            Manda /planes, que es la que cumple el documento.
                            Se conserva la query para no perder la vuelta de Stripe de un
                            pago que se hubiera iniciado antes de este cambio.

                            El componente viejo (pages/OnboardingPage.jsx) se borro en el
                            refactor del 30-09: no lo montaba ninguna ruta y apuntaba a planes
                            ya retirados (reto12en12_gold, reto60, calculadora_jp). Esta en
                            el historial de git. */}
                        <Navigate to={`/planes${window.location.search}`} replace />
                    </ProtectedRoute>
                }
            />

            {/* El regalo del acceso gratis: "te digo cuanto musculo tienes". Pide sesion
                -- hay que registrarse para recibirlo -- pero NO plan: es lo unico de la
                app que se da sin pagar, y ese es justo el punto. */}
            <Route
                path="/mi-cuerpo"
                element={
                    <ProtectedRoute>
                        <MiCuerpoPage />
                    </ProtectedRoute>
                }
            />

            {/* Elegir nivel. Fuera del layout de cliente a proposito: aqui llega tanto
                el que todavia no tiene plan como el que viene a cambiarlo. */}
            {/* CERRADA AL CLIENTE DESDE EL 15-10 (`lib/ventaEnLaApp`): por dirección, a su
                Inicio. Solo entra la vuelta de Stripe, que confirma el pago sin enseñar los
                planes y de ahí al Inicio. */}
            <Route
                path="/planes"
                element={
                    <ProtectedRoute>
                        {seVendenPlanes() ? <PlanesPage />
                            : esLaVueltaDeUnPago(window.location.search) ? <PlanesPage soloLaVuelta />
                            : <Navigate to="/dashboard" replace />}
                    </ProtectedRoute>
                }
            />

            {/* La semana 12: el balance del ciclo y las tres salidas */}
            <Route
                path="/renovacion"
                element={
                    <ProtectedRoute>
                        <RenovacionPage />
                    </ProtectedRoute>
                }
            />

            {/* Client Routes */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <ClientLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<ClientDashboard />} />
                <Route path="routine" element={<CapabilityRoute cap="pestana_rutina"><RoutinePage /></CapabilityRoute>} />
                {/* El registro del entreno del día (T3). Va con la misma llave que la
                    rutina: CAP.RUTINA ya lleva dentro el interruptor `t3_entreno`. */}
                <Route path="entreno" element={<CapabilityRoute cap="rutina"><EntrenoPage /></CapabilityRoute>} />
                <Route path="nutrition" element={<NutritionPage />} />
                {/* Mi semana (rediseño 21-08). Puertas: barra de abajo del móvil y menú
                    lateral del escritorio, las dos detrás de t1_inicio_nuevo. */}
                <Route path="semana" element={<MiSemanaPage />} />
                {/* El horario del cliente (artefacto «Mi semana»: «La primera vez»). Va
                    colgando de Mi semana, que es donde se planifica, y no en Mi perfil:
                    allí están los interruptores, aquí las horas. */}
                <Route path="semana/horario" element={<MiHorarioPage />} />
                {/* El día por dentro (ficha 21): su día colocado en el tiempo. */}
                <Route path="semana/dia/:fecha" element={<MiDiaPage />} />
                {/* «Todos los avisos»: el catálogo de lo que le puede llegar, que es otra
                    cosa que la campana (esa enseña lo de hoy). */}
                <Route path="avisos" element={<TodosLosAvisosPage />} />
                {/* SEGUIMIENTO SE ABRE A TODOS, EN MODO LECTURA (decisión de Jesús del
                    24-08). Pedía «reportes» y 81 clientes no la tienen, así que su
                    historial -- 1.195 reportes de la etapa anterior en 72 de ellos -- y su
                    curva de peso estaban detrás de una puerta cerrada. La llave pasa a ser
                    la del cierre del día, que la lleva todo el mundo; MANDAR un reporte
                    sigue siendo de quien tiene `reportes` y eso lo decide la pantalla (y el
                    servidor, que rechaza el envío igual). */}
                <Route path="reports" element={<CapabilityRoute cap="cierre_dia"><ReportsPage /></CapabilityRoute>} />
                {/* EL CHAT CON EL MISMO CANDADO QUE EL MENÚ (ficha 90 del parte de Gonzalo,
                    4-09). El menú lo esconde con `cap: 'chat'` pero la ruta no lo miraba:
                    quien escribía la dirección abría las puertas de un chat que su plan no
                    lleva (comprobado con una cuenta de Mantenimiento). Lo mismo que hacen
                    las demás rutas con llave: sin ella, al inicio. */}
                {/* Y LA PANTALLA, ABIERTA A TODOS (Francisco, 5-09). Unas horas llevó
                    `cap="chat"` y con eso 112 clientes de 239 -- ELM, Calculadora,
                    Mantenimiento, básica -- no tenían por dónde avisar de un cobro ni de que
                    la app no les carga, que no es lo que se les vende ni lo que se les
                    quiso cerrar. Lo que se cierra es la puerta «Mi plan», y esa la guardan
                    la propia pantalla y el servidor (`routes/messages._puerta_del_canal`).
                    Su frase: «debería poder charlar con uno de los admins por lo menos,
                    pero que se aclare que no tiene entrenador asignado». */}
                <Route path="messages" element={<MessagesPage />} />
                <Route path="profile" element={<ProfilePage />} />
                {/* El asistente, apagado para todos hasta nuevo aviso (Francisco, 26-08).
                    El interruptor `t7_asistente` nace sin fila en `pantallas`, así que da
                    false y la pantalla no se alcanza ni por el menú ni por la dirección.
                    Ni la página ni el backend se tocan: se enciende desde el panel. */}
                <Route path="chatbot" element={<PantallaRoute nombre="t7_asistente"><ChatbotPage /></PantallaRoute>} />
                <Route path="supplements" element={<CapabilityRoute cap="suplementacion"><SupplementsPage /></CapabilityRoute>} />
                {/* El cierre del día, con su llave propia: CAP.CIERRE_DIA aquí (ver
                    lib/planAccess.js) y la feature `cierre_dia` de `derive_features` en el
                    servidor (backend/models/user.py). LAS DOS PUERTAS VAN POR LA MISMA
                    LLAVE desde el 24-08: esta ruta y el cerrojo del `POST /checkins`
                    (backend/routes/checkins.py, `llave = "cierre_dia" if data.type ==
                    "daily" else "reportes"`).
                    POR QUÉ UNA LLAVE NUEVA Y NO «reportes»: el cierre vivía detrás de esa,
                    y los 81 clientes de ELM, Mantenimiento, Calculadora JP y Básica no la
                    tienen, así que no podían contar su día. Darles «reportes» no valía: les
                    encendería de paso un calendario de reportes que su plan no vende. La
                    llave nueva la lleva todo el mundo salvo que a un plan se le ponga
                    `cierre_dia: False` en sus habilitaciones.
                    LA MISMA LLAVE, PERO NO EL MISMO CATÁLOGO: el cerrojo del servidor la
                    busca en el catálogo del código (`plan_grants_feature`), que no lee
                    `db.plan_overrides`, y esta puerta sí los lee. Comprobado en dev: con
                    el cierre apagado para un plan desde el panel, la pantalla ya no abre
                    y el `POST /checkins` de ese mismo cliente sigue respondiendo 200. O
                    sea que la que cierra de verdad al apagar el interruptor del panel es
                    ESTA; el servidor solo para al que no lo lleva por catálogo. Si algún
                    día eso importa, el cambio es `plan_grants_feature_vivo` allí.
                    ESTE COMENTARIO DECÍA LO CONTRARIO Y YA NO ERA VERDAD. Avisaba en
                    mayúsculas de que el servidor seguía pidiendo «reportes» y de que el
                    Guardar devolvía un 403; se arregló el mismo 24-08 y la nota se quedó
                    vieja, que es como se manda al siguiente a tocar lo que ya está bien.
                    Comprobado en dev con una cuenta de plan ELM: la pantalla abre y el
                    `POST /checkins` de tipo `daily` responde 200, mientras el `weekly` --
                    que sí se vende por plan -- le sigue respondiendo 403. Si tocas una de
                    las dos puertas, toca la otra Y ESTA NOTA. */}
                <Route path="checkins" element={<CapabilityRoute cap="cierre_dia"><CheckInsPage /></CapabilityRoute>} />
                <Route path="macro-calculator" element={<MacroCalculatorClientPage />} />
                {/* «TODO TU HISTÓRICO» (pantalla 03 del doc del 7-09): la tabla entera, a la
                    que se llega desde el bloque de la portada. Cuelga de macro-calculator
                    porque es la misma pestaña del menú: no es una entrada nueva. */}
                <Route path="macro-calculator/historico" element={<HistoricoDeMacrosPage />} />
                {/* LOS DOS NOMBRES QUE LA GENTE ESCRIBE (punto 22 del 17-08). `/dashboard/macros`
                    y `/dashboard/my-macros` no estaban declaradas, así que caían en el comodín y
                    acababan en el panel de admin: a Mis macros solo se llegaba pinchando en el
                    menú. Se redirige, no se duplica la pantalla: una ruta, un componente. */}
                <Route path="macros" element={<Navigate to="/dashboard/macro-calculator" replace />} />
                <Route path="my-macros" element={<Navigate to="/dashboard/macro-calculator" replace />} />
                <Route path="foods" element={<FoodSearchPage />} />
                <Route path="ranking" element={<RankingPage />} />
                {/* La revisión suelta: pantalla, no popup (documento del 06-08-2026) */}
                <Route path="revision" element={<RevisionPage />} />
                {/* La pantalla 08 del 7-09: donde vuelve el cliente desde la pasarela
                    despues de pedir su ajuste personalizado. */}
                <Route path="ajuste-personalizado" element={<AjustePersonalizadoPage />} />
            </Route>

            {/* Admin Routes */}
            <Route
                path="/admin"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'trainer']}>
                        <AdminLayout />
                    </ProtectedRoute>
                }
            >
                {/* Rediseño del 21-10: el Dashboard está deshabilitado y el panel abre en Clientes.
                    La pantalla sigue escrita (AdminDashboard); volver es poner aquí su elemento. */}
                <Route index element={<Navigate to="clients" replace />} />
                {/* El catálogo de planes decide qué incluye cada plan y, dentro, qué
                    pantallas están encendidas para todos los clientes. Mismo candado que
                    Usuarios y Cobros, y el backend lo vuelve a comprobar con
                    `get_admin_only_user` por si alguien llama al endpoint a mano. */}
                <Route path="planes" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <AdminPlansPage />
                    </ProtectedRoute>
                } />
                {/* Los ajustes globales (punto 64): las pantallas encendidas para todos y la
                    frase del día. Vivían dentro de Planes, que es otra cosa. Mismo candado:
                    apagar una pantalla a todos los clientes no es cosa de un entrenador. */}
                <Route path="ajustes" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <AdminAjustesPage />
                    </ProtectedRoute>
                } />
                {/* CLIENTES ES EL GESTOR (19-10): el lienzo «Gestor de clientes» sustituye a la lista vieja. */}
                <Route path="clients" element={<GestorDeClientesPage />} />
                <Route path="clients/:clientId" element={<ClientDetailPage />} />
                {/* Las tareas del equipo (doc 19-08, apartado 05): admin y entrenadores,
                    cada uno ve las suyas y las que asignó. */}
                <Route path="tareas" element={<AdminTareasPage />} />
                {/* Los cuatro paneles (doc 19-08, bloque 12). Admin ve los cuatro; el
                    entrenador entra a la misma ruta y la página le enseña solo el suyo.
                    Dirección la protege el backend con get_admin_only_user. */}
                <Route path="paneles" element={<AdminPanelesPage />} />
                <Route path="gestor" element={<Navigate to="/admin/clients" replace />} />
                <Route path="leads" element={<LeadsPage />} />
                <Route path="messages" element={<AdminMessagesPage />} />
                <Route path="routines" element={<AdminRoutinesPage />} />
                <Route path="supplements-catalog" element={<SupplementsCatalogPage />} />
                <Route path="menus" element={<AdminMenusPage />} />
                <Route path="alimentos" element={<AdminFoodSuggestionsPage />} />
                {/* La gestión del equipo. ADMIN Y ENTRENADORES desde el 7-09-2026
                    (Francisco): antes era solo de admin y un entrenador no tenía dónde
                    ver su propia ficha. Le da también la del resto del equipo, con el rol
                    editable; se avisó y se decidió así. El backend hace lo mismo en los
                    cinco endpoints de `/admin/users`. Cobros, Planes y Ajustes siguen
                    cerrados: eso no ha cambiado.
                    DESDE EL 12-10 la ve pero ya no la gestiona: el rol, el plan, la
                    cortesía, las contraseñas y las bajas son de administradores. El
                    entrenador ve al equipo y cambia su propio nombre y teléfono. */}
                <Route path="usuarios" element={<AdminUsersPage />} />
                {/* Lo que ha pagado cada cliente NO es información de entrenamiento, es del
                    negocio: mismo candado que Usuarios, y el backend lo vuelve a comprobar
                    con `get_admin_only_user` por si alguien llama al endpoint a mano. */}
                <Route path="pagos" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <AdminPagosPage />
                    </ProtectedRoute>
                } />
                {/* El acceso desde la hoja de pagos y Stripe (28-09): da y quita acceso a
                    clientes que pagan, así que solo admin (el backend también lo exige). */}
                <Route path="accesos" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <AdminAccesosPage />
                    </ProtectedRoute>
                } />
                {/* El reloj de los clientes, en sombra (30-09): solo admin. */}
                <Route path="reloj" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <AdminRelojPage />
                    </ProtectedRoute>
                } />
                {/* El simulador de semanas (Trello, 30-09): solo lectura, solo admin. */}
                <Route path="simulador" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <AdminSimuladorPage />
                    </ProtectedRoute>
                } />
                {/* La documentación de las reglas, del mismo catálogo que usa el motor
                    (fase 4 sin AC, 07-10): solo lectura, solo admin. */}
                <Route path="documentacion" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <AdminDocumentacionPage />
                    </ProtectedRoute>
                } />
            </Route>

            {/* Cualquier dirección que no exista. Antes mandaba al login sin más, así que
                un enlace roto echaba de la app a un cliente con la sesión abierta: es lo que
                pasaba con el aviso de los macros provisionales. Ahora al login solo va quien
                no ha entrado; al que ya está dentro se le deja en su sitio. */}
            <Route path="*" element={<ADondeSea />} />
        </Routes>
    );
}

function App() {
    return (
        <ThemeProvider>
            <BrowserRouter>
                <AuthProvider>
                    <ConfirmProvider>
                        <OnboardingProvider>
                            {/* Arriba del todo y en todas las pantallas: si el entrenador
                                está dentro de la cuenta de un cliente, tiene que verlo esté
                                donde esté (punto 4.11). */}
                            <BarraActuandoComo />
                            <VueltaDelEnlace>
                                <AppRoutes />
                                {/* Dentro de la vuelta: no sale encima de «Pago aprobado». */}
                                <InstallPrompt />
                                {/* La fecha de una cuenta de prueba (0.2, 02-11): solo si un admin se la activó. */}
                                <FechaDePrueba />
                            </VueltaDelEnlace>
                            {/* LOS AVISOS SE CIERRAN, Y SE CIERRAN SOLOS (Jesús, 15-08,
                                fallo 37): no tenían aspa y alguno se quedó minutos tapando
                                el título de un panel. Seis segundos y una cruz en todos; el
                                que necesite más tiempo lo pide en su `duration`.
                                Y ABAJO A LA DERECHA (doc 57, F6): arriba-centro el aviso
                                caía justo encima de las pestañas de comida y se comía los
                                clics mientras estaba visible.

                                DOS A LA VEZ COMO MUCHO, Y POR ENCIMA DE LA BARRA DE ABAJO
                                (revisión de Nutrición del 1-09, menor 1). En el teléfono los
                                avisos ocupan el ancho de la pantalla y se pegan al borde de
                                abajo, que es justo donde vive la barra de Inicio · Mi semana
                                · Rutina · Más: con tres avisos encima -- y sonner enseña tres
                                por defecto -- no se podía navegar hasta que caducaban o se
                                cerraban a mano. Se miró en 390 px vaciando tres comidas
                                seguidas y los cuatro botones quedaban debajo.
                                La barra mide 72 px y el `env()` cubre el hueco del móvil sin
                                botones. En escritorio no hay barra, así que ahí no cambia. */}
                            <Toaster position="bottom-right" richColors closeButton duration={6000}
                                visibleToasts={2}
                                mobileOffset={{ bottom: 'calc(72px + env(safe-area-inset-bottom) + 12px)',
                                                left: '16px', right: '16px' }} />
                        </OnboardingProvider>
                    </ConfirmProvider>
                </AuthProvider>
            </BrowserRouter>
        </ThemeProvider>
    );
}

export default App;
